import { IconSearch } from "@tabler/icons-react";
import { ColDef, GridOptions, ValueFormatterParams } from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import { Button, Input } from "antd";
import { ProjectContext } from "context/ProjectProvider";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { DateFilter, DateUtils } from "utils/dateutils";
import {
  UpcomingMaterialReportDetailsCellRenderer,
  dueDateCellRenderer,
  dummyColumnDef2,
  gcRepresentativeCellRenderer,
  gcRepresentativeCellRendererWithCustomEdit,
  materialDueDateHeaderComponent,
  riskCellRenderer,
  specSectionLabel,
  specSectionRenderer,
  truncatedTextCellRenderer
} from "components/cell-renders";
import { useCiqFeatureMaterialRiskReportList } from "pages/risk-report/hooks/risk-report";
import { downloadStringAsCSVFile } from "utils/utils";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { useHistory, useParams } from "react-router";
import { InnerIdLinkMaterialRenderer } from "../components/cellRenderer";
import { getMaterialFormattedValues } from "./export-csv-util";

type ReportFilterType = {
  filter: number;
  groupby: string;
  sortBy: string;
};

function UpcomingMaterialsComponent(props: any) {
  const {
    reportFilter,
    setOpenFilterForm,
    projectDetails,
    projectParticipants
  } = props;

  const {
    columnHeaders: { materialHeaderMap }
  } = useContext(ProjectContext);

  const history = useHistory();
  const { projectId } = useParams() as any;
  const gridRef = useRef<AgGridReact<any>>(null);

  const [gridSearchtext, setGridSearchtext] = useState("");
  const [numberOfMaterials, setNumberOfMaterials] = useState<number>(0);
  const [totalNumberOfRows, setTotalNumberOfRows] = useState(0);
  const [isGridReady, setGridReady] = useState(false);
  const [expandedRowCount, setExpandedRowCount] = useState(0);

  const [masterData, setMasterData] =
    useState<Array<{ name: string; id: string; callRecords: Array<any> }>>();

  const { gridData } = useCiqFeatureMaterialRiskReportList({});

  const expandCollapseAll = useCallback(() => {
    const isExpand = expandedRowCount !== totalNumberOfRows; // tmpExpandedItemIds.length > 0;

    gridRef?.current?.api?.forEachNode((node) => {
      node.setExpanded(isExpand);
    });

    if (isExpand) setExpandedRowCount(totalNumberOfRows);
    else setExpandedRowCount(0);
    gridRef?.current?.api?.onGroupExpandedOrCollapsed();
  }, [expandedRowCount, totalNumberOfRows]);

  const onUpdateExpandedRow = () => {
    let count = 0;
    gridRef.current?.api?.forEachNode((node) => {
      if (node.expanded) count = 1 + count;
    });
    gridRef.current?.api?.forEachDetailGridInfo((gridInfo) => {
      gridInfo.api?.forEachNode((node) => {
        if (node.expanded) count = 1 + count;
      });
    });
    setExpandedRowCount(count);
  };

  const updateGridView = (e: ReportFilterType, list: Array<any>) => {
    const dateForFilter = DateUtils.dateTimeObj(e.filter);

    const masterD: Array<{
      name: string;
      id: string;
      callRecords: Array<any>;
    }> = [];

    const groupByItem: any =
      e.groupby === "GroupBy_GCR"
        ? {
            id: "gc_representative",
            firstName: "gc_representative_first_name",
            lastName: "gc_representative_last_name"
          }
        : {
            name: "trade_partner_name"
          };
    let materialCount = 0;
    list?.forEach((data: any) => {
      if (
        data.date_block_materials[0]?.due_date &&
        DateUtils.dateTimeObj(data.date_block_materials[0]?.due_date).diff(
          dateForFilter,
          "days"
        ) <= 0
      ) {
        materialCount += 1;
        const id = data[groupByItem.id]
          ? data[groupByItem.id]
          : data[groupByItem.name];

        const d = masterD.find((x) => x.id === id);
        if (d) {
          d.callRecords.push(data);
        } else {
          let name = data[groupByItem.name];
          if (!name) {
            const unknown =
              e.groupby === "GroupBy_RC"
                ? "Unassigned Responsible Contractor"
                : "Unassigned GC Representative";
            name = data[groupByItem.firstName]
              ? `${data[groupByItem.firstName]} ${data[groupByItem.lastName]}`
              : unknown;
          }

          masterD.push({
            name,
            id,
            callRecords: [data]
          });
        }
      }
    });
    setNumberOfMaterials(materialCount);
    setMasterData(masterD.sort((a, b) => a.name.localeCompare(b.name)));
    setTotalNumberOfRows(materialCount + masterD.length);
  };

  const gridContext: any = useMemo(() => {
    return {
      projectDetails,
      materialHeaderMap,
      projectParticipants
    };
  }, [projectDetails, materialHeaderMap, projectParticipants]);

  const detailCellRenderer = useMemo(() => {
    return UpcomingMaterialReportDetailsCellRenderer;
  }, []);

  const defaultColDef: {} = useMemo(() => {
    return {
      menuTabs: [],
      sortingOrder: ["asc", "desc"],
      suppressColumnsToolPanel: true,
      flex: 1
    };
  }, []);

  const autoGroupColumnDef = useMemo(() => {
    return {
      minWidth: 200
    };
  }, []);

  const columnDefs = useMemo<ColDef[]>(
    () => [
      {
        field: "name",
        cellRenderer: "agGroupCellRenderer",
        valueGetter: ({ data }) => `${data.name} (${data.callRecords.length})`
      }
    ],
    []
  );

  const detailCellRendererParams = useMemo<{
    detailGridOptions: GridOptions;
  }>(() => {
    return {
      // provide the Grid Options to use on the Detail Grid
      detailGridOptions: {
        context: gridContext,
        masterDetail: true,
        detailCellRenderer,
        detailRowAutoHeight: true,
        headerHeight: 36,
        rowHeight: 50,
        groupDefaultExpanded: -1,
        animateRows: false,
        defaultColDef: {
          sortable: true,
          filter: "agSetColumnFilter",
          menuTabs: ["filterMenuTab"],
          resizable: true
        },
        onGridReady(gridApi) {
          if (!reportFilter) return;
          gridApi.api.applyColumnState({
            state: [
              {
                colId: reportFilter.sortBy,
                sort: "asc"
              }
            ]
          });
          gridApi.api?.sizeColumnsToFit();
        },
        onRowGroupOpened() {
          onUpdateExpandedRow();
        },
        columnDefs: [
          dummyColumnDef2,
          {
            colId: "material_id",
            field: "material_id",
            headerName: materialHeaderMap?.material_id || "",
            headerTooltip: materialHeaderMap?.material_id || "",
            width: 150,
            minWidth: 80,
            maxWidth: 200,
            cellRenderer: "agGroupCellRenderer",
            cellRendererParams: {
              innerRenderer: InnerIdLinkMaterialRenderer
            }
          },
          {
            colId: "custom_spec_section",
            field: "custom_spec_section",
            headerName: materialHeaderMap.spec_section_no || "",
            headerTooltip: materialHeaderMap.spec_section_no || "",
            filter: "agSetColumnFilter",
            suppressHeaderMenuButton: false,
            menuTabs: ["filterMenuTab"],
            sortable: true,
            filterParams: {
              valueFormatter: (params: ValueFormatterParams) => {
                try {
                  return params.value.split("-")[0].trim();
                } catch {
                  return params.value;
                }
              }
            },
            cellRenderer: specSectionRenderer,
            cellRendererParams: {
              specNoField: "spec_section_no",
              specNameField: "spec_section_name"
            },
            valueGetter: (params: any) => {
              try {
                const specSectionStr = specSectionLabel({
                  specNumber: params.data.spec_section_no,
                  specName: params.data.spec_section_name
                });

                return specSectionStr;
              } catch (ex) {
                return "";
              }
            },
            comparator: (valueA: string | null, valueB: string | null) => {
              if (!valueA) return 1;
              if (!valueB) return -1;
              return valueA.toLowerCase().localeCompare(valueB.toLowerCase());
            }
          },
          {
            field: "name",
            colId: "name",
            headerName: materialHeaderMap?.name || "",
            headerTooltip: materialHeaderMap?.name || "",
            cellRenderer: truncatedTextCellRenderer
          },
          {
            field: "material_tag",
            colId: "material_tag",
            headerName: materialHeaderMap?.tag_name || "",
            headerTooltip: materialHeaderMap?.tag_name || "",
            cellRenderer: truncatedTextCellRenderer
          },
          {
            field: "status",
            colId: "status",
            valueGetter: ({ data }) => {
              if (
                data?.date_block_materials?.length > 0 &&
                data?.date_block_materials[0]?.milestone_state
              ) {
                return data?.date_block_materials[0]?.milestone_state || "";
              }
              return "";
            },
            headerName: materialHeaderMap?.status || "",
            headerTooltip: materialHeaderMap?.status || "",
            cellRenderer: truncatedTextCellRenderer
          },
          {
            field: "trade_partner_name",
            colId: "trade_partner_name",
            headerName: materialHeaderMap?.trade_partner || "",
            headerTooltip: materialHeaderMap?.trade_partner || "",
            hide: reportFilter.groupby === "GroupBy_RC",
            cellRenderer: truncatedTextCellRenderer
          },
          {
            colId: "gc_representative",
            headerName: materialHeaderMap?.gc_representative || "",
            headerTooltip: materialHeaderMap?.trade_partner || "",
            cellRenderer: (params: any) => {
              const data = gcRepresentativeCellRendererWithCustomEdit(params);
              return data.cellMarkup;
            },
            cellRendererParams: {
              userInput: {
                id: "gc_representative",
                firstName: "gc_representative_first_name",
                lastName: "gc_representative_last_name",
                statusKey: "gc_representative_status"
              }
            },
            valueGetter: (params: any) => {
              const data = gcRepresentativeCellRenderer(params);
              return data.fullName;
            },
            hide: reportFilter.groupby === "GroupBy_GCR"
          },
          {
            colId: "dueDate",
            headerName: materialHeaderMap?.due_date || "",
            headerTooltip: materialHeaderMap?.due_date || "",
            headerComponentParams: materialDueDateHeaderComponent,
            cellRenderer: dueDateCellRenderer,
            comparator: DateFilter.comparator,
            valueGetter: ({ data }) => {
              if (!data || data.date_block_materials.length === 0) return "";
              return data.date_block_materials[0]?.due_date
                ? DateUtils.format(data.date_block_materials[0]?.due_date)
                : "";
            },

            filterValueGetter: ({ data }: any) => {
              const value = data.date_block_materials[0]?.due_date;
              if (!value) return "";
              return DateUtils.format(value);
            }
          },
          {
            colId: "risk_level",
            field: "risk_level",
            headerName: "Risk",
            hide: true,
            cellRenderer: riskCellRenderer
          },
          {
            colId: "risk_assessment",
            headerName: "Risk Assessment",
            hide: true,
            valueGetter: ({ data }: any) =>
              data.date_block_materials.length
                ? data.date_block_materials[0].risk_assessment
                : ""
          },
          {
            colId: "linked_submittals",
            field: "material_submitttal_links",
            headerName: "Linked Submittals",
            hide: true
          },
          {
            colId: "linked_materials",
            field: "material_material_links",
            headerName: "Linked Materials",
            hide: true
          },
          {
            colId: "linked_tasks",
            field: "material_schedule_links",
            headerName: "Impacted Activities",
            hide: true
          },
          {
            colId: "RC_GroupBy",
            field: "responsible_contractor_id",
            rowGroup: false,
            hide: true,
            valueGetter: (param) => param.data.responsible_contractor_name,
            // eslint-disable-next-line react/no-unstable-nested-components
            cellRenderer: ({ data }: any) => (
              <div className="flex font-semibold">
                {data.responsible_contractor_name}
              </div>
            )
          },
          {
            colId: "BIC_GroupBy",
            headerName: materialHeaderMap?.responsible_contractor || "",
            field: "responsible_contractor_name",
            rowGroup: false,
            hide: true,
            valueGetter: ({ data }) => {
              if (!data) return null;
              let fullName = "";
              let userOrg = "";

              if (data.assignee_user_id) {
                fullName = `${data.assignee_first_name} ${data.assignee_last_name}`;
                userOrg = data.assignee_org;
              }
              if (data.assignee_unregistered) {
                fullName = data.assignee_unregistered;
                userOrg = data.assignee_unregistered_org;
              }
              return `${fullName} (${userOrg})`;
            },
            // eslint-disable-next-line react/no-unstable-nested-components
            cellRenderer: ({ value }: any) => (
              <div className="font-medium">{value}</div>
            )
          }
        ] as ColDef[]
      },

      // get the rows for each Detail Grid
      getDetailRowData: (params: any) => {
        params.successCallback(params.data.callRecords);
      }
    };
  }, [detailCellRenderer, gridContext, materialHeaderMap, reportFilter]);

  useEffect(() => {
    gridRef.current?.api?.forEachDetailGridInfo((gridInfo) => {
      gridInfo.api?.setGridOption("quickFilterText", gridSearchtext);
    });
    gridRef.current?.api?.onFilterChanged();
  }, [gridSearchtext]);

  useEffect(() => {
    if (gridData.materials) {
      updateGridView(reportFilter, gridData.materials);
    }
  }, [reportFilter, gridData.materials]);

  useEffect(() => {
    function handleResize() {
      if (isGridReady && gridRef && gridRef.current) {
        gridRef?.current?.api?.sizeColumnsToFit();
      }
    }
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [isGridReady]);

  useEffect(() => {
    gridRef.current?.api?.refreshCells({ force: true });
  }, [gridData.materials, projectParticipants]);

  useEffect(() => {
    if (gridData.materials) {
      setTimeout(() => {
        gridRef.current?.api.refreshCells({
          force: true,
          columns: ["gc_representative"]
        });
      }, 0);
    }
  }, [gridData.materials]);

  const getParamsForCSVExport = useCallback(
    ({ skipColumnHeaders, detailGridInfo, firstTableParams }: any) => {
      const additionalColumns = [
        "trade_partner_name",
        "BIC_GroupBy",
        "risk_level",
        "risk_assessment",
        "linked_materials",
        "linked_submittals",
        "linked_tasks",
        "gc_representative"
      ];

      const allgridCols = detailGridInfo?.api?.getAllGridColumns();
      // console.log("allgridCols ", allgridCols);
      const columnKeysData = firstTableParams?.columnKeys
        ? firstTableParams?.columnKeys
        : allgridCols
            ?.filter((col: any) => {
              const colId = col.getColId();
              return col.isVisible() || additionalColumns?.includes(colId);
            })
            .map((col: any) => col.getColId());

      return {
        columnKeys: columnKeysData,
        processCellCallback(params: any): string {
          return getMaterialFormattedValues(params);
        },
        skipColumnHeaders
      };
    },
    []
  );

  const exportLogAsCSV = useCallback(() => {
    let csvOpString = "";
    let firstTableParams: any = null;
    gridRef.current?.api?.forEachDetailGridInfo(
      (detailGridInfo, index: number) => {
        const getDataParams = getParamsForCSVExport({
          skipColumnHeaders: index > 0,
          detailGridInfo,
          firstTableParams
        });

        if (index === 0) firstTableParams = getDataParams;

        const gridDataAsCsv = detailGridInfo?.api?.getDataAsCsv(getDataParams);
        csvOpString += index > 0 ? `\n${gridDataAsCsv}` : gridDataAsCsv;
      }
    );

    downloadStringAsCSVFile(
      csvOpString,
      `Upcoming Materials_${projectDetails?.name}.csv`
    );
  }, [getParamsForCSVExport, projectDetails?.name]);

  const gotoReports = () => {
    history.push(`/project/${projectId}/reports`);
  };

  return (
    <div className="flex flex-col h-full" data-testid="upcoming-materials-page">
      <div className="px-6 py-6 flex justify-between items-center">
        <div className="font-medium flex items-center">
          <ArrowLeftOutlined
            className="text-black"
            onClick={gotoReports}
            data-testid="upcoming-materials-back"
          />
          <div className="text-xl font-semibold pl-4 pr-2">
            Upcoming Materials
          </div>
          <div
            className="flex h-fit flex-wrap items-center border border-solid border-ciq-gray-100 rounded-xl py-1 px-2 text-black text-opacity-50 text-xs font-normal"
            data-testid="upcoming-materials-count"
          >
            {numberOfMaterials}
          </div>
        </div>
        <div className="flex space-x-3 items-center">
          <Input
            type="text"
            placeholder="Search Materials"
            data-testid="upcoming-materials-search"
            prefix={<IconSearch size={14} />}
            onChange={(event) => {
              setGridSearchtext(event.target.value);
            }}
            allowClear
          />
          <Button
            onClick={() => {
              expandCollapseAll();
            }}
            data-testid="upcoming-materials-expand-toggle"
            className="w-[110px]"
            disabled={!masterData || !masterData.length}
          >
            {expandedRowCount === totalNumberOfRows ? (
              <span>Collapse All</span>
            ) : (
              <span>Expand All</span>
            )}
          </Button>
          <Button
            onClick={() => {
              gridRef?.current?.api?.forEachNode((node) => {
                node.setExpanded(true);
              });

              setTimeout(() => {
                exportLogAsCSV();
              }, 500);
            }}
            disabled={!masterData || !masterData.length}
            data-testid="upcoming-materials-export-csv"
          >
            Export as CSV
          </Button>
          <Button
            type="primary"
            onClick={() => setOpenFilterForm(true)}
            data-testid="upcoming-materials-run-new-report"
          >
            Run New Report
          </Button>
        </div>
      </div>
      <div className="grow ag-theme-alpine">
        <AgGridReact
          ref={gridRef}
          className="ag-theme-alpine"
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          autoGroupColumnDef={autoGroupColumnDef}
          headerHeight={0}
          animateRows={false}
          rowData={masterData}
          suppressContextMenu
          onGridReady={() => {
            setGridReady(true);
          }}
          masterDetail
          detailCellRendererParams={detailCellRendererParams}
          onRowGroupOpened={() => {
            onUpdateExpandedRow();
          }}
          keepDetailRows
          detailRowAutoHeight
          isExternalFilterPresent={() => {
            return true;
          }}
          doesExternalFilterPass={(rowNode: any) => {
            const { detailNode } = rowNode;
            if (detailNode && detailNode.detailGridInfo) {
              const displayedCount =
                detailNode.detailGridInfo?.api?.getDisplayedRowCount();
              if (displayedCount === 0) return false;
            }
            return true;
          }}
          tooltipShowMode="whenTruncated"
        />
      </div>
    </div>
  );
}

export default UpcomingMaterialsComponent;
